/**
 * Removes a JSON object from a string.
 * 
 * @param {string} content - The string containing the JSON data
 * @param {string} [key='v2_campaign'] - The key to look for in the JSON
 * @returns {string} The string with the JSON object removed
 */
export function removeJSONFromMessage(content, key = 'v2_campaign') {
  if (!content || typeof content !== 'string') return content;

  try {
    const searchKey = `"${key}"`;
    if (content.includes(searchKey)) {
      const idxKey = content.indexOf(searchKey);
      const start = content.lastIndexOf('{', idxKey);
      if (start !== -1) {
        let depth = 0;
        let end = start;
        for (; end < content.length; end++) {
          if (content[end] === '{') depth++;
          if (content[end] === '}') depth--;
          if (depth === 0) break;
        }
        if (depth === 0) {
          // Check if the JSON is wrapped in markdown code blocks
          let actualStart = start;
          let actualEnd = end + 1;
          
          // Look backwards for ```json or ``` before the JSON
          const beforeJson = content.substring(0, start);
          const codeBlockMatch = beforeJson.match(/```(?:json)?\s*$/);
          if (codeBlockMatch) {
            actualStart = start - codeBlockMatch[0].length;
          }
          
          // Look forwards for ``` after the JSON
          const afterJson = content.substring(end + 1);
          const closingMatch = afterJson.match(/^\s*```/);
          if (closingMatch) {
            actualEnd = end + 1 + closingMatch[0].length;
          }
          
          // Analyze the context before and after to determine proper spacing
          const beforeContent = content.slice(0, actualStart).trim();
          const afterContent = content.slice(actualEnd).trim();
          
          let replacement = '';
          
          // If there's content before and after, ensure proper paragraph separation
          if (beforeContent && afterContent) {
            // Check if the before content ends with a newline
            const beforeEndsWithNewline = content.slice(0, actualStart).match(/\n\s*$/);
            // Check if the after content starts with a newline
            const afterStartsWithNewline = content.slice(actualEnd).match(/^\s*\n/);
            
            if (beforeEndsWithNewline && afterStartsWithNewline) {
              // Already has proper spacing, just need one newline
              replacement = '\n';
            } else if (beforeEndsWithNewline || afterStartsWithNewline) {
              // One side has spacing, add one more newline
              replacement = '\n\n';
            } else {
              // No spacing on either side, add proper paragraph break
              replacement = '\n\n';
            }
          } else if (beforeContent && !afterContent) {
            // Content before but nothing after, just add newline
            replacement = '\n';
          } else if (!beforeContent && afterContent) {
            // Nothing before but content after, just add newline
            replacement = '\n';
          } else {
            // No content before or after, remove completely
            replacement = '';
          }
          
          // Remove the content and replace with calculated spacing
          const result = content.slice(0, actualStart) + replacement + content.slice(actualEnd);
          
          // Clean up any excessive newlines (more than 2 consecutive)
          return result.replace(/\n{3,}/g, '\n\n');
        }
      }
    }
  } catch (e) {
    // Silent fail - return original content
  }
  return content;
}

export function getCampaignStatusNameAndClass(status) {
  const statusMap = {
    ACTIVE: { name: "Activa", className: "bg-success-light text-success-dark" },
    ENABLED: { name: "Activa", className: "bg-success-light text-success-dark" },
    PAUSED: { name: "Pausado", className: "bg-text-lightest text-text" },
    ARCHIVED: { name: "Archivado", className: "bg-warning text-warning-dark" },
    INACTIVE: { name: "Inactiva", className: "bg-text-lightest text-text" },
    DEFAULT: { name: "Sin definir", className: "bg-text-lightest text-text" },
  };

  return statusMap[status] || statusMap.DEFAULT;
}

/** Max length for campaign name in tables (including "...." when truncated). */
const MAX_CAMPAIGN_NAME_DISPLAY = 37;
const CAMPAIGN_NAME_ELLIPSIS = '....';

/**
 * Truncates a campaign name for table display and returns the string for title (full name on hover).
 * @param {string} name - Campaign name
 * @returns {{ display: string, title: string }} display: text to show (max 37 chars); title: full name for title attribute (empty if not truncated)
 */
export function truncateCampaignNameForDisplay(name) {
  if (name == null || typeof name !== 'string') return { display: '', title: '' };
  const trimmed = name.trim();
  if (trimmed.length <= MAX_CAMPAIGN_NAME_DISPLAY) return { display: trimmed, title: '' };
  return {
    display: trimmed.slice(0, MAX_CAMPAIGN_NAME_DISPLAY - CAMPAIGN_NAME_ELLIPSIS.length) + CAMPAIGN_NAME_ELLIPSIS,
    title: trimmed,
  };
}

export function formatCampaignType(type) {
    if (!type) return "---"

    if (typeof type !== "string") {
      return type;
    }

    return type
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }